import { App, Form, Input } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateVehicleModel,
  useMutationUpdateVehicleModel,
  useQueryGetVehicleMakeLookup,
} from "src/queries/administrator/vehicleSetup.queries";
import type {
  VehicleModelPayload,
  VehicleModelRecord,
} from "src/services/administrator/vehicleSetupService";

interface VehicleModelFormModalProps {
  open: boolean;
  record?: VehicleModelRecord | null;
  onClose: () => void;
}

interface VehicleModelFormValues {
  name: string;
  makeId?: string | number;
  active: string;
  description?: string;
}

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["active", "true", "1"].includes(value.toLowerCase());
  }
  return false;
};

const getLookupItems = (payload: unknown) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const lookupPayload = payload as {
    response?: unknown;
    data?: unknown;
    items?: unknown;
  };

  if (Array.isArray(lookupPayload.response)) return lookupPayload.response;
  if (Array.isArray(lookupPayload.data)) return lookupPayload.data;
  if (Array.isArray(lookupPayload.items)) return lookupPayload.items;
  return [];
};

const VehicleModelFormModal = ({
  open,
  record,
  onClose,
}: VehicleModelFormModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<VehicleModelFormValues>();
  const isEditing = Boolean(record?.id);

  const makeLookupQuery = useQueryGetVehicleMakeLookup(open);
  const createMutation = useMutationCreateVehicleModel();
  const updateMutation = useMutationUpdateVehicleModel();

  const statusOptions = useMemo(
    () => [
      { label: t("active"), value: "true" },
      { label: t("inactive"), value: "false" },
    ],
    [t],
  );

  const makeOptions = useMemo(
    () =>
      getLookupItems(makeLookupQuery.data?.data).map((item) => {
        const option = item as { id?: string | number; name?: string; code?: string };
        return {
          value: option.id,
          label: option.name ?? option.code ?? String(option.id),
        };
      }),
    [makeLookupQuery.data?.data],
  );

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({
      name: record?.name ?? "",
      makeId: record?.make?.id,
      active: String(record ? isActive(record.active) : true),
      description: record?.description ?? "",
    });
  }, [form, open, record]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: VehicleModelPayload = {
      name: values.name.trim(),
      makeId: values.makeId!,
      active: values.active === "true",
      description: values.description?.trim() ?? "",
    };

    try {
      if (isEditing && record?.id) {
        await updateMutation.mutateAsync({ id: record.id, payload });
        message.success(t("vehicle_model_updated"));
      } else {
        await createMutation.mutateAsync(payload);
        message.success(t("vehicle_model_created"));
      }

      handleClose();
    } catch {
      message.error(t("setup_save_failed"));
    }
  };

  return (
    <CustomModal
      open={open}
      title={t(isEditing ? "edit_vehicle_model" : "add_vehicle_model")}
      onCancel={handleClose}
      destroyOnHidden
      width={640}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        <Form.Item
          name="name"
          label={t("vehicle_model_name")}
          rules={[
            { required: true, whitespace: true, message: t("name_required") },
            { max: 64, message: t("name_max_length", { count: 64 }) },
          ]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("enter_name")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="makeId"
          label={t("vehicle_make")}
          rules={[{ required: true, message: t("vehicle_make_required") }]}
          className="mb-3!"
        >
          <CustomSelect
            options={makeOptions}
            placeholder={t("select_vehicle_make")}
            loading={makeLookupQuery.isLoading}
            disabled={makeLookupQuery.isLoading}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          name="active"
          label={t("status")}
          rules={[{ required: true, message: t("status_required") }]}
          className="mb-3!"
        >
          <CustomSelect options={statusOptions} placeholder={t("select")} />
        </Form.Item>

        <Form.Item
          name="description"
          label={t("description")}
          rules={[{ max: 255, message: t("description_too_long") }]}
          className="sm:col-span-2 mb-3!"
        >
          <Input.TextArea
            rows={4}
            placeholder={t("enter_description")}
            maxLength={255}
            className="px-2 py-4 custom-input no-resize"
          />
        </Form.Item>

        <Form.Item className="sm:col-span-2 mb-0!">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            okProps={{ htmlType: "button" }}
            cancelBtnLabel="cancel"
            okBtnLabel={isEditing ? "update" : "add"}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default VehicleModelFormModal;
